import { ExtendedSimCardDetail } from "@/hooks/useExtendedSims";
import {
  SimCardDetail,
  SimCardDetailSimStatus,
  SimCardDetailSimStatusReason,
} from "@/services/model";
import {
  hasNumberPorting,
  hasPendingService,
  needsQRCodeTile,
} from "@/utils/deviceUtils";
import {
  getProductCategory,
  getProductIdFromSim,
  ProductCategory,
} from "@/utils/productMatrix";

/** Max SIMs loaded for client-side priority sorting (large-account safety cap). */
export const PORTAL_SIM_PRIORITY_FETCH_CAP = 100;

/**
 * Display priority for Cockpit / Abos tiles (lower = shown first).
 *
 * 0 Lock requested / eSIM ready to download
 * 1 Suspended (red)
 * 2 Subscription being processed
 * 3 Number porting (WF_ACTIVE_NP or orange)
 * 4 Yellow (expiring)
 * 5–7 Active green: mobile → home → data
 * 8 Other (inactive grey, etc.)
 */
export const getSimDisplayPriority = (
  sim: SimCardDetail | ExtendedSimCardDetail,
): number => {
  if (
    sim.simStatusReason === SimCardDetailSimStatusReason.LOCK_REQUESTED ||
    needsQRCodeTile(sim as ExtendedSimCardDetail)
  ) {
    return 0;
  }

  if (sim.simStatus === SimCardDetailSimStatus.red) {
    return 1;
  }

  if (hasPendingService(sim)) {
    return 2;
  }

  if (
    hasNumberPorting(sim) ||
    sim.simStatus === SimCardDetailSimStatus.orange
  ) {
    return 3;
  }

  if (sim.simStatus === SimCardDetailSimStatus.yellow) {
    return 4;
  }

  if (sim.simStatus === SimCardDetailSimStatus.green) {
    const category: ProductCategory = getProductCategory(
      getProductIdFromSim(sim),
    );

    if (category === "mobile") {
      return 5;
    }
    if (category === "home") {
      return 6;
    }

    return 7;
  }

  return 8;
};

export const compareSimsByDisplayPriority = (
  a: SimCardDetail | ExtendedSimCardDetail,
  b: SimCardDetail | ExtendedSimCardDetail,
): number => {
  const priorityDiff = getSimDisplayPriority(a) - getSimDisplayPriority(b);
  if (priorityDiff !== 0) {
    return priorityDiff;
  }

  // Stable secondary order: newer / higher IDs first (matches API default).
  return (b.simId ?? 0) - (a.simId ?? 0);
};

/**
 * Sorts the first `cap` SIMs by display priority; leaves any remainder in place
 * (API order) so large accounts can still infinite-scroll beyond the cap.
 */
export const applyPrioritySortWithCap = <
  T extends SimCardDetail | ExtendedSimCardDetail,
>(
  sims: T[],
  cap: number = PORTAL_SIM_PRIORITY_FETCH_CAP,
): T[] => {
  if (sims.length <= 1) {
    return sims;
  }

  const head = sims.slice(0, cap);
  const tail = sims.slice(cap);

  return [...head].sort(compareSimsByDisplayPriority).concat(tail);
};
